import classnames from 'classnames';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import {
  TRANSPORT_CATEGORY,
  TRANSPORT_TYPES
} from 'dpl/breeder_search_results/utils/constants';
import Drawer, {
  DrawerItem,
  DrawerItemContent,
  DrawerItemTitle
} from 'dpl/common/components/Drawer';
import GoogleMapMarker from 'dpl/components/GoogleMap/GoogleMapMarker';
import GoogleMapContainer from 'dpl/containers/GoogleMapContainer';
import { getTransportDropOffInfo } from 'dpl/modals/utils/constants';
import { isNumeric } from 'dpl/shared/form/utils';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import locationPinSvg from 'dpl/assets/location_pin.svg';
import locationTargetSvg from 'dpl/assets/location_target.svg';

import CollapsibleList from './CollapsibleList';
import Divider from './Divider';

const { AIR, GROUND, PICKUP } = TRANSPORT_TYPES;
const MAP_HEIGHT = isLessThanBreakpoint('md') ? '180px' : '316px';

function getDeliveryMethodKeys(deliveryMethods) {
  const allMethods = [];

  if (deliveryMethods[AIR]) {
    allMethods.push(...deliveryMethods[AIR]);
  }
  if (deliveryMethods[GROUND]) {
    allMethods.push(...deliveryMethods[GROUND]);
  }
  if (deliveryMethods[PICKUP]) {
    allMethods.push(...deliveryMethods[PICKUP]);
  }

  return allMethods.map(dm => dm.key);
}

export default function LocationAndPickup() {
  const {
    breederFirstName,
    breederAddress,
    searchUserCoordinates,
    breederPickupOptions,
    deliveryMethodsByGroup,
    buyerDistanceInMiles
  } = useSelector(({ server }) => server);

  let transportModalInfo = getTransportDropOffInfo({
    breederFirstName
  });

  if (deliveryMethodsByGroup) {
    const deliveryMethodKeys = getDeliveryMethodKeys(deliveryMethodsByGroup);
    const availableDeliveryMethods = transportModalInfo.filter(tm =>
      deliveryMethodKeys.includes(tm.key)
    );

    transportModalInfo = availableDeliveryMethods;
  }

  const dropOffTransportation = transportModalInfo.filter(
    tm => tm.type === TRANSPORT_CATEGORY.DROPOFF
  );

  const { breederCoordinates, buyerCoordinates } = useMemo(() => {
    if (!breederAddress?.latitude || !breederAddress?.longitude) {
      return { breederCoordinates: null, buyerCoordinates: null };
    }

    return {
      breederCoordinates: {
        lat: breederAddress.latitude,
        lng: breederAddress.longitude
      },
      buyerCoordinates:
        !isEmpty(searchUserCoordinates) &&
        isNumeric(buyerDistanceInMiles) &&
        buyerDistanceInMiles <= 200
          ? {
              lat: searchUserCoordinates[0],
              lng: searchUserCoordinates[1]
            }
          : null
    };
  }, []);

  const coordinates = useMemo(() => {
    return [breederCoordinates, buyerCoordinates].filter(Boolean);
  }, [breederCoordinates, buyerCoordinates]);

  return (
    <div className="LocationAndPickup grid grid-cols-1 md:grid-cols-2 gap-24 md:gap-96">
      <div>
        <h4 className="font-24 md:font-32 lh-title mb2">Location & pick-up</h4>
        <p className="font-16 secondary mb6">
          You can fly or drive to pick up your puppy from {breederFirstName}.
        </p>
        {breederCoordinates && (
          <div
            className="LocationAndPickup__map bg-loading-animation br3 overflow-hidden"
            style={{ height: MAP_HEIGHT }}
          >
            <GoogleMapContainer
              coordinates={coordinates}
              centerOnChange={coordinates.length > 1}
              boundsPadding={16}
              options={{ gestureHandling: 'none' }}
              zoomable={false}
            >
              <GoogleMapMarker
                {...breederCoordinates}
                icon={{ anchor: { x: 18, y: 20 }, url: locationPinSvg }}
              />
              {buyerCoordinates && (
                <GoogleMapMarker
                  {...buyerCoordinates}
                  icon={{ anchor: { x: 31, y: 31 }, url: locationTargetSvg }}
                />
              )}
            </GoogleMapContainer>
          </div>
        )}
      </div>
      <div>
        <div className="hidden md:block">
          <h4 className="font-24 lh-title mb6">
            {/* Zero width space to align the text baseline to the main section heading in the left column */}
            <span className="font-32">&#8203;</span>
            Puppy pick-up locations
          </h4>
          <p className="font-18 mb6">
            {breederFirstName} will coordinate all the details with you.
          </p>
        </div>
        {!isEmpty(breederPickupOptions) && (
          <CollapsibleList
            id="breederPickupOptions"
            maxCollapsedCount={2}
            items={breederPickupOptions.map(({ title, subtitle }) => (
              <div key={title}>
                <p className="fw-medium default-color mb1">{title}</p>
                <p className="secondary">{subtitle}</p>
              </div>
            ))}
            buttonLabels={{
              expanded: 'Show fewer pick-up locations',
              collapsed: 'Show all pick-up locations'
            }}
          />
        )}
        {!isEmpty(dropOffTransportation) && (
          <>
            <Divider className="md:hidden" containerClassName="pv6 pv8-md" />
            <h4 className="font-24 lh-title mb1">Puppy transport options</h4>
            <p className="font-16 md:font-18 secondary mb2">
              {breederFirstName} offers options to safely drop-off your new
              puppy.
            </p>
            {dropOffTransportation.map((tm, idx) => {
              return (
                <Drawer key={idx}>
                  <DrawerItem
                    itemKey={tm.key}
                    className={classnames('pv4', {
                      'bt b--default': idx > 0
                    })}
                  >
                    <DrawerItemTitle
                      className="fw-medium font-16 md:font-18"
                      iconClassName="tertiary"
                      iconOpenName="fetch-chevron-up"
                      iconCloseName="fetch-chevron-down"
                    >
                      {tm.title}
                    </DrawerItemTitle>
                    <DrawerItemContent className="pt2 font-16 md:font-18 secondary">
                      <p>{tm.longDescription}</p>
                      <a
                        className="underline f2"
                        href={tm.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {tm.linkText}
                      </a>
                      {tm.costDescription && (
                        <p className="font-14 md:font-16 mt2">
                          {tm.costDescription}
                        </p>
                      )}
                    </DrawerItemContent>
                  </DrawerItem>
                </Drawer>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
